import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, Plus, Trash2, Pencil, Building2, AlertTriangle, Navigation, Save, Globe, Clock, X } from "lucide-react";
import api from "@/lib/api";
import { Card, PageHeader, Button, Field, Input, Select, Modal, Badge } from "@/components/ui-kit";
import OfficeMapPicker from "@/components/maps/OfficeMapPicker";

/**
 * Iter v32 — Office Locations CRUD page (/lokasi-kantor).
 * Multi-location, scoped per department category.
 * Iter v34j (#7,#9) — Multi-shift (max 3) + Overtime config.
 */

const blank = {
  name: "", latitude: "", longitude: "",
  radius_meters: 100, min_radius_meters: 0, max_radius_meters: 1000,
  department_category: "",
  work_start_time: "08:00", work_end_time: "17:00", standard_work_hours: 8.0,
  shifts: [],  // Iter v34j (#7) — list of {name, start_time, end_time, standard_hours}
  overtime_enabled: true,
  max_overtime_hours: 4.0,
  // Iter 61 — Task 3: late tolerance (toggle + minutes).
  late_tolerance_enabled: false,
  late_tolerance_minutes: 15,
  // Iter v34ao — Break / Face-match config.
  breaks_enabled: true,
  break_min_hours_after_clockin: 8.0,
  max_breaks_per_day: 0,
  face_match_threshold: 0.5,
  tz_name: "Asia/Jakarta",
};

const TZ_OPTIONS = [
  { value: "Asia/Jakarta", label: "WIB (UTC+7) – Asia/Jakarta" },
  { value: "Asia/Makassar", label: "WITA (UTC+8) – Asia/Makassar" },
  { value: "Asia/Jayapura", label: "WIT (UTC+9) – Asia/Jayapura" },
];

const blankShift = (i) => ({
  name: `Shift ${i + 1}`,
  start_time: i === 0 ? "08:00" : (i === 1 ? "16:00" : "00:00"),
  end_time: i === 0 ? "16:00" : (i === 1 ? "00:00" : "08:00"),
  standard_hours: 8.0,
});

export default function OfficeLocations() {
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(blank);
  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(false);
  const [busyGps, setBusyGps] = useState(false);

  const load = async () => {
    try { const r = await api.get("/office-locations"); setRows(r.data); }
    catch { setRows([]); }
  };
  useEffect(() => {
    load();
    api.get("/department-categories").then((r) => setCategories(r.data)).catch(() => setCategories([]));
  }, []);

  const openNew = () => { setEditId(null); setForm(blank); setOpen(true); };
  const openEdit = (r) => {
    setEditId(r.id);
    setForm({
      name: r.name || "", latitude: r.latitude, longitude: r.longitude,
      radius_meters: r.radius_meters ?? 100, min_radius_meters: r.min_radius_meters ?? 0,
      max_radius_meters: r.max_radius_meters ?? 1000,
      department_category: r.department_category || "",
      work_start_time: r.work_start_time || "08:00", work_end_time: r.work_end_time || "17:00",
      standard_work_hours: r.standard_work_hours ?? 8.0,
      shifts: Array.isArray(r.shifts) ? r.shifts : [],
      overtime_enabled: r.overtime_enabled !== false,
      max_overtime_hours: r.max_overtime_hours ?? 4.0,
      late_tolerance_enabled: r.late_tolerance_enabled === true,
      late_tolerance_minutes: r.late_tolerance_minutes ?? 15,
      breaks_enabled: r.breaks_enabled !== false,
      break_min_hours_after_clockin: r.break_min_hours_after_clockin ?? 8.0,
      max_breaks_per_day: r.max_breaks_per_day ?? 0,
      face_match_threshold: r.face_match_threshold ?? 0.5,
      tz_name: r.tz_name || "Asia/Jakarta",
    });
    setOpen(true);
  };

  // Iter v34j (#4) — Multi-sample GPS untuk akurasi lebih baik. Ambil 3 sample
  // dan pilih yang paling akurat (accuracy terkecil).
  const useCurrentGps = () => {
    if (!navigator.geolocation) return toast.error("Browser tidak mendukung GPS");
    setBusyGps(true);
    let best = null;
    let samples = 0;
    const want = 3;
    const opts = { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 };
    const takeSample = () => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (!best || pos.coords.accuracy < best.accuracy) best = pos.coords;
          samples += 1;
          if (samples < want) setTimeout(takeSample, 700);
          else {
            setForm((f) => ({ ...f, latitude: best.latitude.toFixed(7), longitude: best.longitude.toFixed(7) }));
            toast.success(`GPS diset (akurasi terbaik ±${Math.round(best.accuracy)}m dari ${samples} sample)`);
            setBusyGps(false);
          }
        },
        (err) => { toast.error("Gagal GPS: " + err.message); setBusyGps(false); },
        opts,
      );
    };
    takeSample();
  };

  const addShift = () => {
    if (form.shifts.length >= 3) return toast.error("Maksimal 3 shift");
    setForm({ ...form, shifts: [...form.shifts, blankShift(form.shifts.length)] });
  };
  const updateShift = (i, k, v) => {
    const next = [...form.shifts]; next[i] = { ...next[i], [k]: v }; setForm({ ...form, shifts: next });
  };
  const removeShift = (i) => setForm({ ...form, shifts: form.shifts.filter((_, j) => j !== i) });

  const save = async () => {
    const payload = {
      ...form,
      latitude: parseFloat(form.latitude), longitude: parseFloat(form.longitude),
      radius_meters: Number(form.radius_meters), min_radius_meters: Number(form.min_radius_meters),
      max_radius_meters: Number(form.max_radius_meters), standard_work_hours: Number(form.standard_work_hours),
      max_overtime_hours: Number(form.max_overtime_hours),
      late_tolerance_minutes: Number(form.late_tolerance_minutes) || 0,
      breaks_enabled: !!form.breaks_enabled,
      break_min_hours_after_clockin: Number(form.break_min_hours_after_clockin) || 8,
      max_breaks_per_day: Number(form.max_breaks_per_day) || 0,
      face_match_threshold: Math.max(0.30, Math.min(0.65, Number(form.face_match_threshold) || 0.5)),
      shifts: form.shifts.map((s) => ({
        ...s,
        standard_hours: Number(s.standard_hours),
        // Iter 61 — per-shift override of late_tolerance_minutes (optional).
        late_tolerance_minutes: s.late_tolerance_minutes !== undefined && s.late_tolerance_minutes !== ""
          ? Number(s.late_tolerance_minutes)
          : undefined,
      })),
    };
    if (Number.isNaN(payload.latitude) || Number.isNaN(payload.longitude)) return toast.error("Latitude/Longitude harus angka valid");
    if (payload.radius_meters < 0) return toast.error("Radius tidak boleh negatif");
    if (payload.shifts.length > 3) return toast.error("Maksimal 3 shift");
    try {
      if (editId) await api.put(`/office-locations/${editId}`, payload);
      else await api.post("/office-locations", payload);
      toast.success("Lokasi disimpan");
      setOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal simpan"); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Hapus lokasi "${r.name}"?`)) return;
    try { await api.delete(`/office-locations/${r.id}`); toast.success("Dihapus"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal hapus"); }
  };

  const usedCategories = new Set(rows.filter((r) => r.department_category).map((r) => r.department_category));
  const availableCategories = categories.filter((c) => !usedCategories.has(c.name) || c.name === form.department_category);

  return (
    <div className="space-y-6" data-testid="office-locations-page">
      <PageHeader overline="Pengaturan · Absensi" title="Lokasi Kantor"
        description="Atur lokasi kantor per kategori departemen + shift (max 3) + lembur. Radius absen pakai nilai 'Radius (m)' utama." />

      <div className="flex justify-between items-center">
        <Badge tone="stone"><Building2 className="h-3 w-3 inline" /> {rows.length} lokasi terdaftar</Badge>
        <Button onClick={openNew} data-testid="office-loc-new"><Plus className="h-3.5 w-3.5" /> Tambah Lokasi</Button>
      </div>

      <Card className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-[10px] uppercase tracking-widest text-stone-500">
            <tr>
              <th className="text-left px-3 py-3">Nama Lokasi</th>
              <th className="text-left px-3 py-3">Kategori Departemen</th>
              <th className="text-left px-3 py-3">Koordinat</th>
              <th className="text-right px-3 py-3">Radius</th>
              <th className="text-center px-3 py-3">Shift / Jam</th>
              <th className="text-center px-3 py-3">Lembur</th>
              <th className="text-center px-3 py-3">Toleransi</th>
              <th className="text-right px-3 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {rows.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-12 text-center text-xs text-stone-400">Belum ada lokasi. Klik &quot;Tambah Lokasi&quot;.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-stone-50/40" data-testid={`office-loc-row-${r.id}`}>
                <td className="px-3 py-2.5 font-medium text-stone-900">{r.name}{r.id === "primary" && <Badge tone="stone" className="ml-2">fallback</Badge>}</td>
                <td className="px-3 py-2.5 text-xs text-stone-700">{r.department_category || <span className="text-stone-400">(semua / fallback)</span>}</td>
                <td className="px-3 py-2.5 font-mono text-xs text-stone-600">{Number(r.latitude).toFixed(5)}, {Number(r.longitude).toFixed(5)}</td>
                <td className="px-3 py-2.5 text-right font-mono text-xs">{r.radius_meters}m</td>
                <td className="px-3 py-2.5 text-center text-xs text-stone-600">
                  {Array.isArray(r.shifts) && r.shifts.length > 0 ? (
                    <div className="space-y-0.5">
                      {r.shifts.map((s, i) => <div key={i}><b>{s.name}</b>: {s.start_time}-{s.end_time}</div>)}
                    </div>
                  ) : (
                    <>{r.work_start_time}-{r.work_end_time} · <b>{r.standard_work_hours}j</b></>
                  )}
                </td>
                <td className="px-3 py-2.5 text-center text-xs">
                  {r.overtime_enabled !== false ? (
                    <span className="text-emerald-700">Aktif · max {r.max_overtime_hours ?? 4}j</span>
                  ) : (
                    <span className="text-stone-400">Nonaktif</span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-center text-xs" data-testid={`office-loc-tol-${r.id}`}>
                  {r.late_tolerance_enabled === true ? (
                    <span className="text-amber-700">Aktif · {r.late_tolerance_minutes ?? 15} mnt</span>
                  ) : (
                    <span className="text-stone-400">Nonaktif</span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap">
                  <button onClick={() => openEdit(r)} className="text-stone-500 hover:text-terra-700 px-2" title="Edit" data-testid={`office-loc-edit-${r.id}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  {r.id !== "primary" && (
                    <button onClick={() => remove(r)} className="text-stone-500 hover:text-red-700 px-2" title="Hapus" data-testid={`office-loc-del-${r.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card className="p-3 text-xs text-stone-600 bg-amber-50 border-amber-200">
        <AlertTriangle className="h-3.5 w-3.5 inline text-amber-600" /> Radius absen pakai nilai <b>Radius (m)</b>. Karyawan yang absen lebih dari radius akan masuk status <code>pending_approval</code> sampai admin/HR menyetujui.
      </Card>

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Edit Lokasi Kantor" : "Tambah Lokasi Kantor"} size="lg">
        <div className="space-y-3" data-testid="office-loc-modal">
          <Field label="Nama Lokasi *">
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Contoh: Batching Plant Jaya, Proyek Kemayoran" data-testid="office-loc-name" />
          </Field>
          <Field label="Kategori Departemen" hint="Kosongkan untuk lokasi fallback (semua karyawan)">
            <Select value={form.department_category} onChange={(e) => setForm({ ...form, department_category: e.target.value })} data-testid="office-loc-category">
              <option value="">(fallback — semua karyawan)</option>
              {availableCategories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Latitude *">
              <Input type="number" step="any" value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} placeholder="-2.9909" data-testid="office-loc-lat" />
            </Field>
            <Field label="Longitude *">
              <Input type="number" step="any" value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} placeholder="104.7569" data-testid="office-loc-lng" />
            </Field>
          </div>
          <Button variant="outline" onClick={useCurrentGps} disabled={busyGps} className="w-full" data-testid="office-loc-use-gps">
            <Navigation className="h-3.5 w-3.5" /> {busyGps ? "Mengambil 3x sample GPS..." : "Gunakan GPS Saat Ini (3x sample, akurasi terbaik)"}
          </Button>
          <OfficeMapPicker
            lat={form.latitude}
            lng={form.longitude}
            radius={form.radius_meters}
            onChange={(lat, lng) => setForm((f) => ({ ...f, latitude: lat, longitude: lng }))}
          />
          <Field label={<span className="flex items-center gap-1.5"><Globe className="h-3 w-3" /> Zona Waktu Lokasi (untuk pencatatan absensi)</span>}>
            <Select value={form.tz_name} onChange={(e) => setForm({ ...form, tz_name: e.target.value })} data-testid="office-loc-tz">
              {TZ_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </Select>
          </Field>
          <Field label="Radius Absen (m) *" hint="Karyawan harus dalam radius ini saat absen. Minimum 0m.">
            <Input type="number" min="0" value={form.radius_meters} onChange={(e) => setForm({ ...form, radius_meters: e.target.value })} data-testid="office-loc-radius" />
          </Field>

          {/* Iter v34j (#7) — Shift Management */}
          <Card className="p-3 border-stone-200 bg-stone-50/30">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-stone-700 inline-flex items-center gap-1"><Clock className="h-3 w-3" /> Shift Kerja (max 3)</label>
              {form.shifts.length < 3 && (
                <Button variant="outline" className="!py-1 !px-2 text-xs" onClick={addShift} data-testid="office-loc-add-shift"><Plus className="h-3 w-3" />Tambah Shift</Button>
              )}
            </div>
            {form.shifts.length === 0 ? (
              <p className="text-xs text-stone-500">Belum ada shift. Klik &quot;Tambah Shift&quot; atau gunakan jam kerja standar di bawah.</p>
            ) : (
              <div className="space-y-2">
                {form.shifts.map((s, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center bg-white border border-stone-200 p-2" data-testid={`office-loc-shift-${i}`}>
                    <Input className="col-span-4" placeholder="Nama shift" value={s.name} onChange={(e) => updateShift(i, "name", e.target.value)} data-testid={`shift-name-${i}`} />
                    <Input className="col-span-3" type="time" value={s.start_time} onChange={(e) => updateShift(i, "start_time", e.target.value)} data-testid={`shift-start-${i}`} />
                    <Input className="col-span-3" type="time" value={s.end_time} onChange={(e) => updateShift(i, "end_time", e.target.value)} data-testid={`shift-end-${i}`} />
                    <Input className="col-span-1 font-mono-num" type="number" step="0.5" value={s.standard_hours} onChange={(e) => updateShift(i, "standard_hours", e.target.value)} title="Std jam" data-testid={`shift-hours-${i}`} />
                    <button onClick={() => removeShift(i)} className="col-span-1 text-stone-400 hover:text-red-700 mx-auto" data-testid={`shift-remove-${i}`}><X className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <div className="grid grid-cols-3 gap-3">
            <Field label="Jam Mulai (default)">
              <Input type="time" value={form.work_start_time} onChange={(e) => setForm({ ...form, work_start_time: e.target.value })} />
            </Field>
            <Field label="Jam Selesai (default)">
              <Input type="time" value={form.work_end_time} onChange={(e) => setForm({ ...form, work_end_time: e.target.value })} />
            </Field>
            <Field label="Std. Jam">
              <Input type="number" step="0.5" value={form.standard_work_hours} onChange={(e) => setForm({ ...form, standard_work_hours: e.target.value })} />
            </Field>
          </div>

          {/* Iter v34j (#9) — Overtime Config */}
          <Card className="p-3 border-stone-200 bg-stone-50/30">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-stone-700">Pengaturan Lembur</label>
              <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={form.overtime_enabled} onChange={(e) => setForm({ ...form, overtime_enabled: e.target.checked })} data-testid="office-loc-ot-enabled" />
                Aktifkan Lembur
              </label>
            </div>
            {form.overtime_enabled && (
              <Field label="Maksimum jam lembur per hari" hint="0 = tanpa batas">
                <Input type="number" min="0" step="0.5" value={form.max_overtime_hours} onChange={(e) => setForm({ ...form, max_overtime_hours: e.target.value })} data-testid="office-loc-max-ot" />
              </Field>
            )}
          </Card>

          {/* Iter 61 (#3) — Late Tolerance Config */}
          <Card className="p-3 border-stone-200 bg-stone-50/30">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-stone-700">Toleransi Keterlambatan</label>
              <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.late_tolerance_enabled}
                  onChange={(e) => setForm({ ...form, late_tolerance_enabled: e.target.checked })}
                  data-testid="office-loc-late-tol-enabled"
                />
                Aktifkan Toleransi
              </label>
            </div>
            {form.late_tolerance_enabled ? (
              <Field
                label="Maks. menit terlambat sebelum ditandai TERLAMBAT"
                hint="Karyawan yang clock-in dalam batas ini tetap dihitung tepat waktu."
              >
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={form.late_tolerance_minutes}
                  onChange={(e) => setForm({ ...form, late_tolerance_minutes: e.target.value })}
                  data-testid="office-loc-late-tol-minutes"
                />
              </Field>
            ) : (
              <p className="text-xs text-stone-500">
                Nonaktif — setiap clock-in setelah jam mulai dianggap TERLAMBAT (mode ketat).
              </p>
            )}
          </Card>

          {/* Iter v34ao (#2) — Break/Istirahat Config */}
          <Card className="p-3 border-stone-200 bg-stone-50/30">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-stone-700">Istirahat / Rest Break</label>
              <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.breaks_enabled !== false}
                  onChange={(e) => setForm({ ...form, breaks_enabled: e.target.checked })}
                  data-testid="office-loc-breaks-enabled"
                />
                Izinkan Karyawan Istirahat
              </label>
            </div>
            {form.breaks_enabled !== false ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Threshold Istirahat (jam sejak Clock-In)" hint="Tombol 'Istirahat' baru muncul setelah karyawan bekerja ≥ N jam.">
                  <Input
                    type="number" min="1" step="0.5"
                    value={form.break_min_hours_after_clockin}
                    onChange={(e) => setForm({ ...form, break_min_hours_after_clockin: e.target.value })}
                    data-testid="office-loc-break-threshold"
                  />
                </Field>
                <Field label="Maks. istirahat per hari (0 = tak terbatas)" hint="Jumlah maksimum sesi istirahat yang boleh diambil karyawan dalam satu hari.">
                  <Input
                    type="number" min="0" step="1"
                    value={form.max_breaks_per_day}
                    onChange={(e) => setForm({ ...form, max_breaks_per_day: e.target.value })}
                    data-testid="office-loc-break-max"
                  />
                </Field>
              </div>
            ) : (
              <p className="text-xs text-stone-500">Karyawan tidak bisa menekan tombol Istirahat.</p>
            )}
          </Card>

          {/* Iter v34ao (#1) — Face Match Tolerance */}
          <Card className="p-3 border-stone-200 bg-stone-50/30">
            <label className="text-xs font-semibold uppercase tracking-wider text-stone-700">Toleransi Pencocokan Wajah</label>
            <Field label={`Face Match Threshold — ${Number(form.face_match_threshold ?? 0.5).toFixed(2)}`}
              hint="Semakin RENDAH semakin ketat (default 0.50). Bila karyawan sering ditolak 'data tidak sesuai', naikkan ke 0.55–0.60. Rentang aman: 0.30–0.65.">
              <input
                type="range"
                min="0.30" max="0.65" step="0.01"
                value={form.face_match_threshold ?? 0.5}
                onChange={(e) => setForm({ ...form, face_match_threshold: e.target.value })}
                data-testid="office-loc-face-threshold"
                className="w-full accent-terra-600"
              />
              <div className="flex justify-between text-[10px] text-stone-500 mt-1">
                <span>0.30 (sangat ketat)</span>
                <span>0.50 (default)</span>
                <span>0.65 (longgar)</span>
              </div>
            </Field>
          </Card>

          <div className="flex justify-end gap-2 pt-4 border-t border-stone-100">
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={save} data-testid="office-loc-save"><Save className="h-3.5 w-3.5" /> Simpan</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
